import React from 'react'
import { Component, LocaleCode } from '../types/component'
import { getStyleFromConfig } from '../utils/styles'
import { Header } from './Header'
import { Text } from './Text'
import { Button } from './Button'
import { Image } from './Image'
import { Video } from './Video'
import { Hero } from './Hero'
import { Container } from './Container'
import { Row } from './Row'
import { ProductCard } from './ProductCard'
import { ProductGrid } from './ProductGrid'
import { Navigation } from './Navigation'
import { Cart } from './Cart'
import { Wishlist } from './Wishlist'
import { ProductDetail } from './ProductDetail'
import { Features } from './Features'
import { Pricing } from './Pricing'
import { Stats } from './Stats'
import { Team } from './Team'
import { CTA } from './CTA'
import { Footer } from './Footer'
import { LogoCloud } from './LogoCloud'
import { Divider } from './Divider'
import { Spacer } from './Spacer'
import { Card } from './Card'
import { Search } from './Search'
import { Checkout } from './Checkout'
import { ProductCarousel } from './ProductCarousel'
import { Testimonials } from './Testimonials'
import { FAQ } from './FAQ'
import { ContactForm } from './ContactForm'
import { Section } from './Section'
import { HtmlElement } from './HtmlElement'

interface ComponentRendererProps {
  component: Component
  locale?: LocaleCode
  storeId: string
  apiUrl: string
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

/**
 * Main component renderer that dispatches to specific component types
 */
export function ComponentRenderer({
  component,
  locale = 'en',
  storeId,
  apiUrl,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate
}: ComponentRendererProps) {
  const style = getStyleFromConfig(component.style, component.layout)

  // Handle hidden components
  if (component.hidden) {
    return null
  }

  const commonProps = {
    component,
    locale,
    storeId,
    apiUrl,
    style,
  }

  const commerceProps = {
    ...commonProps,
    cartId,
    wishlistId,
    onCartUpdate,
    onWishlistUpdate,
  }

  switch (component.type) {
    case 'header':
      return <Header {...commonProps} />

    case 'text':
      return <Text {...commonProps} />

    case 'button':
      return <Button {...commonProps} />

    case 'image':
      return <Image {...commonProps} />

    case 'video':
      return <Video {...commonProps} />

    case 'hero':
      return <Hero {...commonProps} />

    case 'container':
      return <Container {...commerceProps} />

    case 'row':
      return <Row {...commerceProps} />

    case 'product-card':
      return <ProductCard {...commerceProps} />

    case 'product-grid':
      return <ProductGrid {...commerceProps} />

    case 'navigation':
      return <Navigation {...commerceProps} />

    case 'cart':
      return <Cart {...commerceProps} />

    case 'wishlist':
      return <Wishlist {...commerceProps} />

    case 'product-detail':
      return <ProductDetail {...commerceProps} />

    case 'features':
      return <Features {...commonProps} />

    case 'pricing':
      return <Pricing {...commonProps} />

    case 'stats':
      return <Stats {...commonProps} />

    case 'team':
      return <Team {...commonProps} />

    case 'cta':
      return <CTA {...commonProps} />

    case 'footer':
      return <Footer {...commonProps} />

    case 'logo-cloud':
      return <LogoCloud {...commonProps} />

    case 'divider':
      return <Divider {...commonProps} />

    case 'spacer':
      return <Spacer {...commonProps} />

    case 'card':
      return <Card {...commonProps} />

    case 'search':
      return <Search {...commonProps} />

    case 'checkout':
      return <Checkout {...commonProps} />

    case 'product-carousel':
      return <ProductCarousel {...commonProps} />

    case 'testimonials':
      return <Testimonials {...commonProps} />

    case 'faq':
      return <FAQ {...commonProps} />

    case 'contact-form':
      return <ContactForm {...commonProps} />

    case 'section':
      return <Section {...commerceProps} />

    case 'html-element':
      return (
        <HtmlElement component={component} style={style}>
          {component.children?.map((child) => (
            <ComponentRenderer
              key={child.id}
              component={child}
              locale={locale}
              storeId={storeId}
              apiUrl={apiUrl}
              cartId={cartId}
              wishlistId={wishlistId}
              onCartUpdate={onCartUpdate}
              onWishlistUpdate={onWishlistUpdate}
            />
          ))}
        </HtmlElement>
      )

    case 'timeline':
    case 'accordion':
    case 'tabs':
      // Placeholder for not-yet-implemented components
      return (
        <div style={style}>
          <p style={{ color: '#888', fontSize: '14px', padding: '20px' }}>
            Component type "{component.type}" not yet implemented in renderer
          </p>
        </div>
      )

    default:
      return (
        <div style={style}>
          <p style={{ color: '#ff0000', fontSize: '14px', padding: '20px' }}>
            Unknown component type: {component.type}
          </p>
        </div>
      )
  }
}
